/**
 * Totales de serie para contadores (Explorer / viewport) durante carga progresiva.
 */

/**
 * Number of Frames (0028,0008) desde metadata de file / image.
 * @param {object} f
 * @returns {number}
 */
export function resolveFileNumberOfFrames(f) {
  if (!f) return 0
  let max = 0
  const bump = (v) => {
    const n = Number(v)
    if (Number.isFinite(n) && n > max) max = n
  }
  bump(f.numberOfFrames)
  if (f.series) bump(f.series.numberOfFrames)
  try {
    const img = f.image
    if (img && img.data) {
      if (typeof img.data.intString === 'function') {
        bump(img.data.intString('x00280008'))
      }
      if (max <= 1 && typeof img.data.string === 'function') {
        bump(parseInt(img.data.string('x00280008'), 10))
      }
    }
  } catch (e) {
    /* ignore */
  }
  return max
}

/**
 * Mejor total conocido para una lista de archivos de la misma serie.
 * Prefiere metadata (manifest/Orthanc) sobre el tamaño parcial del buffer.
 * Serie monofile multiframe: usa Number of Frames.
 * @param {object[]} seriesFiles
 * @returns {number}
 */
export function resolveSeriesTotalImages(seriesFiles) {
  if (!Array.isArray(seriesFiles) || seriesFiles.length === 0) return 0
  let metaMax = 0
  let frameMax = 0
  for (let i = 0; i < seriesFiles.length; i++) {
    const f = seriesFiles[i]
    const s = f && f.series
    if (s) {
      const t = Number(s.totalImages)
      if (Number.isFinite(t) && t > metaMax) metaMax = t
      const c = Number(s.instanceCount)
      if (Number.isFinite(c) && c > metaMax) metaMax = c
    }
    const nf = resolveFileNumberOfFrames(f)
    if (nf > frameMax) frameMax = nf
  }
  // 1 SOP multiframe (XA/US/cine): el total visible son los frames
  if (seriesFiles.length === 1 && frameMax > 1) {
    return Math.max(metaMax, frameMax)
  }
  return Math.max(metaMax, seriesFiles.length)
}

/**
 * Cuántas instancias ya están descargadas / listas para viewport.
 * En monofile multiframe, si el SOP está listo cuenta todos los frames.
 * @param {object[]} seriesFiles
 * @param {(f: object) => boolean} [isDownloaded]
 * @returns {number}
 */
export function countDownloadedSeriesImages(seriesFiles, isDownloaded) {
  if (!Array.isArray(seriesFiles) || seriesFiles.length === 0) return 0
  const test =
    typeof isDownloaded === 'function'
      ? isDownloaded
      : (f) => !!(f && !f.error && f.skeleton !== true && (f.imageId || f.image))
  if (seriesFiles.length === 1) {
    const f = seriesFiles[0]
    if (!test(f)) return 0
    const nf = resolveFileNumberOfFrames(f)
    if (nf > 1) {
      // Progreso por frame (Oviyam / cine): framesReadyCount o Set.
      if (f.framesReadySet && typeof f.framesReadySet.has === 'function') {
        return Math.min(nf, f.framesReadySet.size)
      }
      const fr = Number(f.framesReadyCount)
      if (Number.isFinite(fr) && fr >= 0) return Math.min(nf, Math.floor(fr))
      return nf
    }
    return 1
  }
  let n = 0
  for (let i = 0; i < seriesFiles.length; i++) {
    if (test(seriesFiles[i])) n += 1
  }
  return n
}

/**
 * Estampa Number of Frames en file + series y alza totalImages si aplica.
 * @param {object} fileData
 * @param {number} frames
 */
export function stampNumberOfFramesOnFile(fileData, frames) {
  const n = Number(frames)
  if (!fileData || !Number.isFinite(n) || n < 1) return
  fileData.numberOfFrames = n
  if (!fileData.series) fileData.series = {}
  fileData.series.numberOfFrames = n
  if (n > 1) {
    const prev = Number(fileData.series.totalImages) || 0
    fileData.series.totalImages = Math.max(prev, n)
    if (
      !fileData.series.instanceCount ||
      fileData.series.instanceCount < fileData.series.totalImages
    ) {
      fileData.series.instanceCount = fileData.series.totalImages
    }
  }
}

export default resolveSeriesTotalImages
